import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { Button } from "@/components/Button";
import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { IConsultantProfile, unlinkConsultantProfile } from "@/data/consultant-profiles";
import { refresh } from "@/data/session";
import { fetchProfilesConnectivityToApplicationsAndContracts } from "@/data/users";
import { Flex, IDialogModal, inDialogModal } from "@/eworkitComponents";
import { SessionContext } from "@/infrastructure/Session";
import { CloseButton } from "@/views/components/CloseButton";
import { StyledModal } from "@/views/components/Modal";
import { useToasts } from "@/views/components/Toast/useToasts";
import { router } from "@/views/router";

import "./_settings-modal.scss";

interface IUnlinkModalProps extends IDialogModal {
  payload: IConsultantProfile;
  onClose: (shouldRefresh?: boolean) => void;
}

interface IModalProps extends IDialogModal {
  onUnlink: () => void;
}

const Modal: React.FC<IModalProps> = ({ onClose, onUnlink }) => {
  const { t } = useTranslation("session");

  return (
    <StyledModal>
      <Flex direction="column" padding={["lg"]} innerMargin="lg">
        <Flex justify="space-between">
          <Header weight="bold">{t("CONSULTANT_PROFILES.DETAIL.UNLINK.HEADER")}</Header>
          <CloseButton onClick={onClose} testName="close" />
        </Flex>
        <Flex justify="center">
          <div className="modal__img-container" />
        </Flex>
        <Flex direction="column" innerMargin="md">
          <Text>{t("CONSULTANT_PROFILES.DETAIL.UNLINK.TEXT")}</Text>
          <Flex justify="flex-end" innerMargin="md">
            <Button variant="secondary" onClick={() => onClose()} size="lg" testName="cancel">
              <Text>{t("CONSULTANT_PROFILES.DETAIL.DELETE.BUTTON_CANCEL")}</Text>
            </Button>
            <Button onClick={onUnlink} size="lg" testName="unlink">
              <Text>{t("CONSULTANT_PROFILES.DETAIL.UNLINK.BUTTON_CONFIRM")}</Text>
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </StyledModal>
  );
};

const UnlinkModalComponent = inDialogModal(Modal);

export const UnlinkModal: React.FC<IUnlinkModalProps> = React.memo(({ open, payload, onClose }) => {
  const { addToast } = useToasts();
  const history = useHistory();
  const Session = useContext(SessionContext);
  const User = useContext(UserContext);
  const { t } = useTranslation(["session", "common"]);

  const {
    contextCompany,
    contextConsultantProfileId,
    getFirstAvailableProfile,
    setConsultantProfileContext,
    hasCompanyManagerPrivileges,
  } = User;

  const handleUnlinkUser = async () => {
    try {
      const connectivityRes = await fetchProfilesConnectivityToApplicationsAndContracts(payload.id);

      if (connectivityRes.hasActiveAppOrContract) {
        addToast({
          text: t("CONSULTANT_PROFILES.DETAIL.UNLINK.MESSAGES.PROFILE_HAS_ACTIVE_APPLICATION"),
          type: "warning",
        });
        return;
      }

      await unlinkConsultantProfile({
        consultantProfileId: payload.id,
        companyId: contextCompany && contextCompany.id,
      });

      if (contextConsultantProfileId === payload.id) {
        const availableProfile = getFirstAvailableProfile(contextConsultantProfileId);
        if (!availableProfile) {
          throw new Error("No available profile found.");
        }
        await setConsultantProfileContext(availableProfile.id);

        const isManager = hasCompanyManagerPrivileges();

        if (isManager) {
          history.push(router.get("companies.team").path);
        } else {
          history.push(
            router.get("consultant-profiles.detail", {
              consultantProfileId: availableProfile.id,
            }).path,
          );
        }
      } else {
        const isManager = hasCompanyManagerPrivileges();

        if (isManager) {
          history.push(router.get("companies.team").path);
        }
      }

      const sessionUser = await refresh();

      Session.update(sessionUser, () => {
        addToast({
          text: t("CONSULTANT_PROFILES.DETAIL.UNLINK.MESSAGES.SUCCESS"),
          type: "success",
        });
      });
    } catch {
      addToast({
        text: t("common:ERROR_MESSAGES.GENERAL"),
        type: "warning",
      });
    } finally {
      onClose(true);
    }
  };

  return <UnlinkModalComponent onClose={onClose} onUnlink={handleUnlinkUser} open={open} size="xs" />;
});
